// src/components/AddImplant.js
import { useState } from 'react';
import { api } from '../api';

export default function AddImplant({ schema, onSuccess, onError }) {
  const [name, setName]         = useState('');
  const [mfr, setMfr]           = useState('');
  const [kNumber, setKNumber]   = useState('');
  const [features, setFeatures] = useState({});
  const [saving, setSaving]     = useState(false);

  const setFeature = (category, key, val) => {
    setFeatures(prev => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [key]: val },
    }));
  };

  const featureCount = Object.values(features)
    .flatMap(cat => Object.values(cat || {}))
    .filter(Boolean).length;

  const handleSubmit = async () => {
    if (!name.trim()) { onError('Implant name is required'); return; }
    if (featureCount === 0) { onError('Select at least one feature'); return; }
    setSaving(true);
    try {
      await api.addImplant({ implant_name: name.trim(), manufacturer: mfr.trim(), k_number: kNumber.trim(), features });
      onSuccess(`✓ "${name}" added to live database`);
      setName(''); setMfr(''); setKNumber(''); setFeatures({});
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Add Implant Manually</h1>
        <p style={s.sub}>
          Use this for well-known implants not in the FDA 510(k) pipeline — Straumann, Nobel, Zimmer, etc.
        </p>
      </div>

      <div style={s.form}>
        <div style={s.section}>
          <div style={s.sectionTitle}>Identity</div>
          <div style={s.row}>
            <div style={s.group}>
              <label style={s.label}>Implant Name *</label>
              <input style={s.input} value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Nobel Active RP" />
            </div>
            <div style={s.group}>
              <label style={s.label}>Manufacturer</label>
              <input style={s.input} value={mfr} onChange={e => setMfr(e.target.value)}
                placeholder="e.g. Nobel Biocare" />
            </div>
            <div style={{ ...s.group, flex: 0.8 }}>
              <label style={s.label}>510(k) Number</label>
              <input style={s.input} value={kNumber} onChange={e => setKNumber(e.target.value)}
                placeholder="e.g. K123456 (optional)" />
            </div>
          </div>
        </div>

        {schema && (
          <div style={s.section}>
            <div style={s.sectionTitle}>
              Features
              <span style={s.featureCount}>{featureCount} selected</span>
            </div>
            <div style={s.featureGrid}>
              {Object.entries(schema).map(([category, keys]) => (
                <div key={category} style={s.featureCategory}>
                  <div style={s.catLabel}>{category.replace(/_/g, ' ')}</div>
                  <div style={s.catKeys}>
                    {keys.map(key => {
                      const on = !!(features[category]?.[key]);
                      return (
                        <button
                          key={key}
                          style={{ ...s.featureBtn, ...(on ? s.featureBtnOn : {}) }}
                          onClick={() => setFeature(category, key, !on)}
                        >
                          {on && <span style={s.check}>✓ </span>}
                          {key.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={s.footer}>
          <div style={s.footerNote}>
            This implant will be added directly to the live database and immediately available for identification.
          </div>
          <button style={s.submitBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Adding…' : '+ Add to Database'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  header: { marginBottom: 28 },
  title:  { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0, letterSpacing: '-0.03em' },
  sub:    { fontSize: 13, color: '#64748b', marginTop: 4 },
  form:   { background: '#111827', border: '1px solid #1e2530', borderRadius: 12, overflow: 'hidden' },
  section: { padding: '20px 24px', borderBottom: '1px solid #1e2530' },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14,
    display: 'flex', alignItems: 'center', gap: 10,
  },
  featureCount: {
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
    color: '#60a5fa', background: '#172554', padding: '2px 7px', borderRadius: 4,
    fontWeight: 400, textTransform: 'none', letterSpacing: 0,
  },
  row:   { display: 'flex', gap: 14 },
  group: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' },
  input: {
    padding: '9px 12px', background: '#0a0d14', border: '1px solid #2d3748',
    borderRadius: 7, color: '#e2e8f0', fontSize: 13,
    fontFamily: "'Inter', sans-serif", outline: 'none',
  },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16 },
  featureCategory: {},
  catLabel: {
    fontSize: 10, color: '#475569', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: 6, fontWeight: 600,
  },
  catKeys: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  featureBtn: {
    fontSize: 11, padding: '4px 9px', borderRadius: 5,
    border: '1px solid #2d3748', background: '#0a0d14',
    color: '#64748b', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
  },
  featureBtnOn: {
    background: '#1e3a5f', border: '1px solid #1d4ed8', color: '#93c5fd',
  },
  check: { color: '#60a5fa' },
  footer: {
    padding: '16px 24px', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', background: '#0a0d14',
  },
  footerNote: { fontSize: 12, color: '#475569', maxWidth: 420 },
  submitBtn: {
    padding: '10px 28px', background: '#1d4ed8', border: 'none',
    borderRadius: 7, color: '#fff', cursor: 'pointer', fontSize: 13,
    fontWeight: 600, fontFamily: "'Inter', sans-serif",
  },
};
